/**
 * Living KPI catalog — shell fetch client.
 *
 * Thin wrappers over the BFF monitoring edge routes:
 *   GET  /api/monitoring/kpis        → catalog metadata (ETag/304, short cache)
 *   GET  /api/monitoring/kpis/pins   → this user's pinned ids
 *   PUT  /api/monitoring/kpis/pins   → replace pinned ids
 *   GET  /api/monitoring/snapshot    → sparkline series for the pinned ids
 *
 * Catalog is ETag-cached in-memory so Home + the catalog tab share one payload
 * for the session; the browser still revalidates via If-None-Match.
 */
import type { KpiCatalog, KpiSeries } from './kpiFormat';

const BASE = (import.meta as any).env?.VITE_BFF_BASE_URL || '';

function csrf(): string {
  return document.cookie.match(/(?:^|; )_eid_csrf_v1=([^;]+)/)?.[1] || '';
}

interface CatalogCacheEntry {
  etag?: string;
  value: KpiCatalog;
}
let _catalogCache: CatalogCacheEntry | null = null;

export interface CatalogResult {
  catalog: KpiCatalog | null;
  error?: string;
  status?: number;
}

/** Fetch the KPI catalog (metadata only). Uses a conditional GET + memo cache. */
export async function fetchCatalog(signal?: AbortSignal): Promise<CatalogResult> {
  try {
    const headers: Record<string, string> = { Accept: 'application/json' };
    if (_catalogCache?.etag) headers['If-None-Match'] = _catalogCache.etag;
    const res = await fetch(`${BASE}/api/monitoring/kpis`, {
      credentials: 'include',
      headers,
      signal,
    });
    if (res.status === 304 && _catalogCache) {
      return { catalog: _catalogCache.value };
    }
    if (!res.ok) {
      if (_catalogCache) return { catalog: _catalogCache.value, error: `HTTP ${res.status}`, status: res.status };
      return { catalog: null, error: `HTTP ${res.status}`, status: res.status };
    }
    const value = (await res.json()) as KpiCatalog;
    _catalogCache = { etag: res.headers.get('ETag') || undefined, value };
    return { catalog: value };
  } catch (err: any) {
    if (err?.name === 'AbortError') return { catalog: _catalogCache?.value ?? null };
    if (_catalogCache) return { catalog: _catalogCache.value, error: err?.message || 'Network error' };
    return { catalog: null, error: err?.message || 'Network error' };
  }
}

/** Fetch the current user's pinned KPI ids. */
export async function fetchPins(signal?: AbortSignal): Promise<string[]> {
  try {
    const res = await fetch(`${BASE}/api/monitoring/kpis/pins`, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal,
    });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.pins) ? data.pins.filter((x: unknown) => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

export interface SavePinsResult {
  pins: string[] | null;
  error?: string;
  status?: number;
}

/** Replace the user's pinned KPI ids. Returns the persisted set on success. */
export async function savePins(pins: string[]): Promise<SavePinsResult> {
  try {
    const token = csrf();
    const res = await fetch(`${BASE}/api/monitoring/kpis/pins`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': token,
        'X-CSRFToken': token,
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ pins }),
    });
    if (!res.ok) {
      let detail: string | undefined;
      try {
        detail = (await res.json())?.detail;
      } catch {
        /* ignore parse error */
      }
      return { pins: null, error: detail || `HTTP ${res.status}`, status: res.status };
    }
    const data = await res.json();
    return { pins: Array.isArray(data?.pins) ? data.pins : [] };
  } catch (err: any) {
    return { pins: null, error: err?.message || 'Network error' };
  }
}

/** Fetch sparkline series for the given ids. Empty ids → empty series. */
export async function fetchSnapshot(
  ids: string[],
  opts: { window?: number; step?: number; signal?: AbortSignal } = {},
): Promise<KpiSeries> {
  const list = ids.filter(Boolean).slice(0, 20);
  if (list.length === 0) return {};
  const window = opts.window ?? 900;
  const step = opts.step ?? 30;
  try {
    const url = `${BASE}/api/monitoring/snapshot?keys=${encodeURIComponent(list.join(','))}&window=${window}&step=${step}`;
    const res = await fetch(url, {
      credentials: 'include',
      headers: { Accept: 'application/json' },
      signal: opts.signal,
    });
    if (!res.ok) return {};
    const data = await res.json();
    return data && typeof data.series === 'object' ? (data.series as KpiSeries) : {};
  } catch {
    return {};
  }
}

/** Test seam: clear the in-memory catalog cache. */
export function _resetCatalogCache(): void {
  _catalogCache = null;
}
